import api from './api';
import { cachedGet } from '../utils/lookupCache';

/**
 * Pending EDI Orders (Order Management) — the bucket list + one bucket's drill-down.
 * Backed by app/api/api_v1/endpoints/pending_edi_orders.py (both GET, read-only).
 * See the "Pending EDI Orders" page (pages/order-management/PendingEdiOrders.tsx).
 */

/** One bucket: a distinct (edihandle, billto) with its pending count + mapped CSRs. */
export interface EdiBucket {
  edihandle: string;
  billto: number;
  billtoname: string;
  /** Pending orders in this bucket THAT THE REQUESTED LCs OWN. Scoped server-side, so
   *  an LC's number is their own, not the handle's — see getEdiBuckets. */
  count: number;
  edicsr: string; // comma-joined active CSR usernames mapped to this edihandle
  /** Orders here that no lane rule claims, on a handle that HAS lane rules. Always 0 on
   *  a handle nobody has lane-scoped, so a non-zero value is a real coverage gap. */
  unclaimed?: number;
  /** Accept/reject deadline highlight for the MOST-URGENT unaccepted order in the bucket
   *  ('' | 'yellow' | 'orange' | 'red'), + its minutes remaining (null = nothing urgent). */
  urgency?: string;
  min_remaining?: number | null;
}

export interface EdiBucketsResponse {
  data: EdiBucket[];
  total: number;
}

/**
 * A drill-down row. The shape is dynamic — the bespoke query returns every
 * edi_orders column plus the computed/denormalized ones (commodity, size,
 * pickup/delivery/terminate names, resolved scac/port, invalidtemplate,
 * dupeprolist, missing-product/location and multistop flags). Columns are driven by the edi_orders
 * field-definition registry; keys here are the raw column names those defs point at.
 */
export type EdiResultRow = Record<string, any> & {
  recno: number;
  /** Customer master LC assignment; independent of the EDI lane owner. */
  customer_lc?: string;
};

export interface EdiResultsResponse {
  data: EdiResultRow[];
  total: number;
  edihandle: string;
  billto: number;
}

/** Stable key for a bucket (edihandle + billto), used for selection/highlighting. */
export const bucketKey = (edihandle: string, billto: number | string): string => `${edihandle}|${billto}`;

/**
 * Fetch the pending EDI order buckets, optionally scoped to the orders one or more LCs
 * own.
 *
 * THE SCOPE IS APPLIED SERVER-SIDE, and it has to be. Ownership is per-order now (an LC
 * can be assigned a handle narrowed by customer / delivery location / move type), so
 * filtering in the browser could only hide whole buckets — it could never make `count`
 * say 13 when the handle holds 100. Passing the scope down is what makes the number
 * right.
 *
 * Pass nothing for the unscoped manager / full-page view: every bucket, full counts.
 * Values may be plain usernames or a tilde-joined LC-group member list; the backend
 * expands groups.
 */
export async function getEdiBuckets(lcUsernames?: string[]): Promise<EdiBucketsResponse> {
  const res = await api.get('/pending-edi-orders/buckets', {
    params: lcUsernames?.length ? { lc_usernames: lcUsernames } : undefined,
    // Repeat the key (?lc_usernames=a&lc_usernames=b) — FastAPI reads a List[str] Query
    // that way; axios would otherwise send lc_usernames[]=a, which binds to nothing.
    paramsSerializer: { indexes: null },
  });
  return res.data as EdiBucketsResponse;
}

/**
 * Fetch the drill-down rows for one bucket.
 *
 * Takes the SAME scope as getEdiBuckets and must be called with it, or the drill shows
 * rows the bucket did not count. Every batch action posts explicit recnos taken from
 * these rows, so scoping here also scopes Accept / Reject / Delete / Get Template For
 * All / Create All.
 */
export async function getEdiBucketResults(
  edihandle: string,
  billto: number,
  lcUsernames?: string[],
): Promise<EdiResultsResponse> {
  const res = await api.get('/pending-edi-orders/results', {
    params: { edihandle, billto, ...(lcUsernames?.length ? { lc_usernames: lcUsernames } : {}) },
    paramsSerializer: { indexes: null },
  });
  return res.data as EdiResultsResponse;
}

/** An LC filter option. Group values are a tilde-joined member list (isGroup: true). */
export interface EdiLcOption {
  value: string;
  label: string;
  isGroup?: boolean;
}

/**
 * The board's LC filter options — SCOPE-INDEPENDENT, on purpose.
 *
 * These used to be derived from the LC names inside the buckets that had just loaded.
 * That worked while a bucket carried its whole handle's roster, but breaks once the
 * response is itself LC-scoped: filter to Anna and Anna is the only name left in the
 * dropdown, so you cannot switch to Bob without clearing the filter first. Its own
 * read, unaffected by what is filtered.
 *
 * Population is who works EDI (an edi_csrs membership) — NOT the order-LC list behind
 * /lc-dashboards/filter-options, which would offer LCs with no EDI handles at all.
 */
export async function getEdiLcOptions(): Promise<EdiLcOption[]> {
  const res = await cachedGet<{ data?: EdiLcOption[] }>('/pending-edi-orders/lc-options');
  return (res.data?.data ?? []) as EdiLcOption[];
}

/**
 * Result of a batch action: the recnos actually changed, plus any that were skipped
 * (already accepted/rejected/deleted, or now attached to a pro) with the reason.
 */
export interface EdiActionResult {
  applied: number[];
  skipped: { recno: number; reason: string }[];
}

/** Accept pending EDI orders (acceptreject='A'). Only blank rows are accepted; already
 *  accepted/rejected rows come back in `skipped`. */
export async function acceptEdiOrders(recnos: number[]): Promise<EdiActionResult> {
  const res = await api.post('/pending-edi-orders/accept', { recnos });
  return res.data as EdiActionResult;
}

/** Reject pending EDI orders (acceptreject='R' + reason). Only blank rows are rejected. */
export async function rejectEdiOrders(recnos: number[], reason: string): Promise<EdiActionResult> {
  const res = await api.post('/pending-edi-orders/reject', { recnos, reason });
  return res.data as EdiActionResult;
}

/** Delete pending EDI orders (deleted='Y' + reason). */
export async function deleteEdiOrders(recnos: number[], reason: string): Promise<EdiActionResult> {
  const res = await api.post('/pending-edi-orders/delete', { recnos, reason });
  return res.data as EdiActionResult;
}

/** Clear accept/reject on a pending EDI order (undo an accept). Per-record only. */
export async function clearAcceptEdiOrders(recnos: number[]): Promise<EdiActionResult> {
  const res = await api.post('/pending-edi-orders/clear-accept', { recnos });
  return res.data as EdiActionResult;
}

/**
 * A candidate template for Get Template. The backend returns the V1 edi_gettemplate columns
 * (pronumber, movetype, templatetype/rate-zone, csr, cname, drayrate, invoicetotal,
 * dest/orig name+address, comments, mas90code/sage tag).
 */
export type EdiTemplateCandidate = Record<string, any> & { pronumber: string };

export interface EdiTemplateCandidatesResponse {
  data: EdiTemplateCandidate[];
  total: number;
}

/** Fetch the Get Template drop-down candidates for one EDI order's partner rules. Shared by
 *  single Get Template and Get Template For All (same params — the bucket's shared values). */
export async function getEdiTemplateCandidates(
  edihandle: string, billto: number, movetype: string, deliveryrecno: number,
): Promise<EdiTemplateCandidatesResponse> {
  const res = await api.get('/pending-edi-orders/template-candidates', {
    params: { edihandle, billto, movetype, deliveryrecno },
  });
  return res.data as EdiTemplateCandidatesResponse;
}

/**
 * Stamp / change / clear the template on pending EDI orders (ineligible rows come back in
 * `skipped`). `template=''` clears. `onlyBlank=true` (Get Template For All) touches only
 * blank-template rows; `onlyBlank=false` (single edit) may overwrite or clear a set one.
 */
export async function applyEdiTemplate(
  recnos: number[], template: string, onlyBlank: boolean = true,
): Promise<EdiActionResult> {
  const res = await api.post('/pending-edi-orders/get-template', { recnos, template, only_blank: onlyBlank });
  return res.data as EdiActionResult;
}

/**
 * One candidate pro for the Attach dialog (V1 edi_attachpro). The backend returns the pro
 * plus the display columns the modal shows (bol, container, WO#, and how many EDI files are
 * already attached to that pro). Same dupe logic as the board's dupeprolist column, so the
 * candidate list equals the board count.
 */
export interface EdiAttachCandidate {
  pronumber: number;
  bol: string;
  container: string;
  billtorefno: string;
  numedifiles: number;
}

export interface EdiAttachCandidatesResponse {
  data: EdiAttachCandidate[];
  total: number;
  recno: number;
}

/** Fetch the pros matching one pending EDI order — the Attach dialog's dupe list. */
export async function getEdiAttachCandidates(recno: number): Promise<EdiAttachCandidatesResponse> {
  const res = await api.get('/pending-edi-orders/attach-candidates', { params: { recno } });
  return res.data as EdiAttachCandidatesResponse;
}

/**
 * One field in the attach preview. `will_update` rows carry from/to + reason; `wont_update`
 * rows carry the pro value (kept) vs the edi value (differs).
 */
export interface EdiAttachDiffField {
  field: string;
  label: string;
  from?: string;
  to?: string;
  reason?: string;
  pro?: string;
  edi?: string;
  /** wont_update only: whether the user may opt to overwrite this field on attach (false for
   *  billto / customer / leg locations, which attach never changes). */
  overridable?: boolean;
}

/**
 * Dry-run of attaching an EDI order to a pro (V1 edi_saveattachpro's update blocks + $diffmsg):
 * `will_update` = fields the attach will change; `wont_update` = fields that differ but are
 * kept. `found` is false when the entered pronumber isn't a real pro.
 */
export interface EdiAttachPreview {
  pronumber: string;
  found: boolean;
  edihandle: string;
  will_update: EdiAttachDiffField[];
  wont_update: EdiAttachDiffField[];
  forced_fields: string[];
}

/** Fetch the attach preview (what will / won't change) for a chosen pro. */
export async function getEdiAttachPreview(recno: number, pronumber: string): Promise<EdiAttachPreview> {
  const res = await api.get('/pending-edi-orders/attach-preview', { params: { recno, pronumber } });
  return res.data as EdiAttachPreview;
}

export interface EdiAttachResult {
  recno: number;
  pronumber: string;
  applied: string[];
  order_written: boolean;
  legs_written: number;
  invoice_written: boolean;
  effects_executed: number;
  /** Whether the inbound EDI file was attached to the pro as a document (best-effort). */
  document_attached?: boolean;
}

/**
 * Attach a pending EDI order to an existing pro (V1 edi_saveattachpro) — sets pronumber and
 * enriches the pro's fields through the saverules engine. `overrideFields` are the extra
 * differing fields the user opted to overwrite beyond the locked defaults.
 */
export async function attachEdiOrder(
  recno: number, pronumber: string, overrideFields: string[] = [],
): Promise<EdiAttachResult> {
  const res = await api.post('/pending-edi-orders/attach', {
    recno, pronumber, override_fields: overrideFields,
  });
  return res.data as EdiAttachResult;
}

/**
 * The create-from-EDI form model (V1 edi_createpro). `fields` holds each editable order
 * column pre-filled EDI-value-else-template-value; `from_edi` is the read-only column; the
 * pickup/delivery/terminate blocks are the mapped CMS locations (delivery is display-only).
 * `carrier_editable` is the INITIAL state (dfwlocal etc. → false); the FE recomputes it from
 * CARRIER_DISPATCHERS as the dispatcher changes. `eligible`/`reason` mirror the save gate.
 */
/** How the server resolved one location field for a create (pickup / terminate).
 *  `source`: 'mapping' (an EDI Pickup Location Mapping row matched — `locked`, the server
 *  ignores anything else you send; pickup only), 'choice' (the template and the EDI name
 *  different locations — `code` is 0 and `candidates` holds both, template first; the user
 *  must pick or the create is refused), 'edi' / 'template' (only one side had one), 'none'
 *  (neither — the cloned template leg's own value stands). */
export interface EdiLocationResolution {
  code: number;
  name: string;
  source: 'mapping' | 'choice' | 'edi' | 'template' | 'none';
  locked: boolean;
  mapping_recno: number;
  candidates: { origin: 'template' | 'edi'; code: number; name: string }[];
}

export interface EdiCreatePreview {
  recno: number;
  eligible: boolean;
  reason: string | null;
  edihandle: string;
  editype: string;
  movetype: string;
  is_import: boolean;
  template: string;
  billto: number;
  customercode: number;
  /** The TEMPLATE's broker. `fields.brokercode` is the editable, effective one
   *  (template's when it has one, else the EDI's — see the create service). */
  brokercode: string;
  carrier_editable: boolean;
  fields: Record<string, any>;
  /** Resolved pickup (see edi_create_service.resolve_edi_pickup).
   *  `source`: 'mapping' (an EDI Pickup Location Mapping row matched — `locked`, the
   *  server ignores anything else you send), 'choice' (template and EDI disagree —
   *  `code` is 0 and `candidates` holds both; the user must pick), 'edi' / 'template'
   *  (only one side had one), 'none'. */
  pickup: EdiLocationResolution;
  delivery: { code: number; name: string };
  /** Same contract as `pickup`, minus 'mapping' — there is no terminate override table. */
  terminate: EdiLocationResolution;
  from_edi: {
    container: string; bol: string; scac: string;
    /** Broker name off the EDI row — shown even when the template's broker won the pre-fill. */
    broker: string;
    lbs: any; kgs: any; notes: string[];
  };
}

/** Fetch the create-from-EDI form model for one pending order. */
export async function getEdiCreatePreview(recno: number): Promise<EdiCreatePreview> {
  const res = await api.get('/pending-edi-orders/create-preview', { params: { recno } });
  return res.data as EdiCreatePreview;
}

export interface EdiCreateResult {
  recno: number;
  pronumber: number;
  document_attached?: boolean;
  userMessages?: { text: string; level: string }[];
}

/** Create a new pro from a pending EDI order (V1 edi_savepro), through the saverules engine. */
export async function createEdiOrder(
  recno: number, fields: Record<string, any>,
  pickupcode?: number | null, terminatecode?: number | null,
): Promise<EdiCreateResult> {
  const res = await api.post('/pending-edi-orders/create', {
    recno, fields, pickupcode: pickupcode ?? null, terminatecode: terminatecode ?? null,
  });
  return res.data as EdiCreateResult;
}

export interface EdiCreateItem {
  recno: number;
  fields: Record<string, any>;
  pickupcode?: number | null;
  terminatecode?: number | null;
}

export interface EdiCreateAllResult {
  created: EdiCreateResult[];
  skipped: { recno: number; reason: string }[];
  created_count: number;
  skipped_count: number;
}

/** Batch-create pros from every eligible pending EDI order (V1 edi_createallorders). */
export async function createAllEdiOrders(
  items: EdiCreateItem[], useOneMasterpro: boolean = false,
): Promise<EdiCreateAllResult> {
  const res = await api.post('/pending-edi-orders/create-all', {
    items, use_one_masterpro: useOneMasterpro,
  });
  return res.data as EdiCreateAllResult;
}

/**
 * LC username -> full name options for the board's LC filter labels. Sourced from the
 * shared LC filter options (the same `lcs` list the LC Dashboards LC filter uses, via
 * app/utils/lc_filters.py), individuals only — group entries (tilde-delimited username
 * lists) aren't individual CSRs so they can't label an edi_csrs username. Best-effort:
 * the caller falls back to raw usernames if this fails.
 */
export async function getLcNameOptions(): Promise<{ value: string; label: string }[]> {
  const res = await cachedGet<{ lcs?: { value: string; label: string; isGroup?: boolean }[] }>('/lc-dashboards/filter-options');
  const lcs = (res.data?.lcs || []) as { value: string; label: string; isGroup?: boolean }[];
  return lcs.filter((l) => !l.isGroup).map((l) => ({ value: l.value, label: l.label }));
}
